import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import * as Tooltip from '@radix-ui/react-tooltip';
import { TrendingDown } from 'lucide-react';
import { Block, BlockType, ExitType, isTerminalBlock, ValidationError, ValidationWarning } from '../../types';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Ban,
  BarChart2,
  Target,
  Trash2,
  Clock,
  BarChart3,
  Code,
  Copy,
  Lock,
  Unlock,
  Power,
  PowerOff,
  Activity,
  TrendingUp,
  Settings,
  Calculator,
  Database,
  BarChart,
  Workflow,
} from 'lucide-react';

interface StrategyBlockProps {
  block: Block;
  isSelected: boolean;
  onSelect: (event: React.MouseEvent) => void;
  onStartConnection: (blockId: string, portType: 'input' | 'output') => void;
  onCompleteConnection: (blockId: string, portType: 'input' | 'output') => void;
  onDelete?: (blockId: string) => void;
  onDuplicate?: (block: Block) => void;
  onToggleLock?: (blockId: string) => void;
  onToggleDisable?: (blockId: string) => void;
  isEffectivelyDisabled?: boolean;
  connectingPortType?: 'input' | 'output' | null;
  readonly?: boolean;
  validationErrors?: ValidationError[];
  validationWarnings?: ValidationWarning[];
}

const StrategyBlock: React.FC<StrategyBlockProps> = ({
  block,
  isSelected,
  onSelect,
  onStartConnection,
  onCompleteConnection,
  onDelete,
  onDuplicate,
  onToggleLock,
  onToggleDisable,
  isEffectivelyDisabled = false,
  connectingPortType,
  readonly = false,
  validationErrors = [],
  validationWarnings = [],
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: block.id,
  });

  const [isLocked, setIsLocked] = React.useState(block.data.locked || false);
  // S91 — derive disabled straight from the block prop (the source of truth,
  // updated by handleToggleDisable) so the dim + icon never desync on
  // undo/redo or external edits. STRATEGY_CONFIG can't be disabled.
  const isDisabled = (block.data.disabled ?? false) && block.type !== BlockType.STRATEGY_CONFIG;
  // S92 — also dim blocks that are TRANSITIVELY disabled (a condition whose
  // source block was disabled) so the user sees the whole filter chain go
  // inactive. The Power/PowerOff icon below still reflects the EXPLICIT flag.
  const isDimmed = isDisabled || (isEffectivelyDisabled && block.type !== BlockType.STRATEGY_CONFIG);
  const [blockWidth, setBlockWidth] = React.useState(300);
  // Hover-tooltip open state. We control it so we can SUPPRESS the tooltip for
  // the currently-selected block: its details already show in the TSL Block
  // Editor panel, and the tooltip (a body-level portal at z-50) would
  // otherwise render on top of that panel and cover its content.
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);

  // Debug ID for logging
  const dbgId = block.id.slice(0, 4);

  // TSL-compliant width calculation
  React.useEffect(() => {
    if (contentRef.current && headerRef.current) {
      const contentWidth = contentRef.current.scrollWidth;
      const headerWidth = headerRef.current.scrollWidth;
      const baseWidth = 300;

      const requiredWidth = Math.max(contentWidth + 32, headerWidth + 16);
      const newWidth = Math.max(baseWidth, Math.min(400, requiredWidth));

      setBlockWidth(newWidth);
    }
  }, [block.data.name, block.data.description, block.type]);

  const dragHandleProps =
    isLocked || readonly ? {} : { ...attributes, ...listeners };

  // TSL: Check if this block can have connections
  const canHaveInputConnections = block.type !== BlockType.STRATEGY_CONFIG;
  const canHaveOutputConnections =
    block.type !== BlockType.STRATEGY_CONFIG && !isTerminalBlock(block.type);

  const handleInputPortMouseDown = (e: React.MouseEvent) => {
    if (readonly || !canHaveInputConnections) return;
    e.stopPropagation();
    console.log('↓ mousedown IN ', dbgId);
    onStartConnection(block.id, 'input');
  };

  const handleInputPortMouseUp = (e: React.MouseEvent) => {
    if (readonly || !canHaveInputConnections) return;
    e.stopPropagation();
    console.log('↑ mouseup   IN ', dbgId);
    onCompleteConnection(block.id, 'input');
  };

  const handleOutputPortMouseDown = (e: React.MouseEvent) => {
    if (readonly || !canHaveOutputConnections) return;
    e.stopPropagation();
    console.log('↓ mousedown OUT', dbgId);
    onStartConnection(block.id, 'output');
  };

  const handleOutputPortMouseUp = (e: React.MouseEvent) => {
    if (readonly || !canHaveOutputConnections) return;
    e.stopPropagation();
    console.log('↑ mouseup   OUT', dbgId);
    onCompleteConnection(block.id, 'output');
  };

  const getBlockProperties = () => {
    switch (block.type) {
      case BlockType.STRATEGY_CONFIG:
        return {
          title: 'Strategy Configuration',
          icon: <Settings className="w-4 h-4" />,
          bgColor: 'bg-cyan-100/[1]',
          borderColor: 'border-cyan-300',
          textColor: 'text-cyan-800',
          selectedBgColor: 'bg-cyan-50/[1]',
          selectedBorderColor: 'border-cyan-400',
          tslInfo: 'TSL Foundation Block (No Connections)',
        };
      case BlockType.PRICE_DATA:
        return {
          title: 'Price Data (OHLCV)',
          icon: <Database className="w-4 h-4" />,
          bgColor: 'bg-indigo-100/[1]',
          borderColor: 'border-indigo-300',
          textColor: 'text-indigo-800',
          selectedBgColor: 'bg-indigo-50/[1]',
          selectedBorderColor: 'border-indigo-400',
          tslInfo: 'TSL Data Source Block',
        };
      case BlockType.MOVING_AVERAGE:
        return {
          title: 'Moving Average',
          icon: <TrendingUp className="w-4 h-4" />,
          bgColor: 'bg-indigo-100/[1]',
          borderColor: 'border-indigo-300',
          textColor: 'text-indigo-800',
          selectedBgColor: 'bg-indigo-50/[1]',
          selectedBorderColor: 'border-indigo-400',
          tslInfo: 'TSL Technical Indicator → Logic Block Required',
        };
      case BlockType.MOMENTUM_OSCILLATOR:
        return {
          title: 'Momentum/Oscillator',
          icon: <Activity className="w-4 h-4" />,
          bgColor: 'bg-purple-100/[1]',
          borderColor: 'border-purple-300',
          textColor: 'text-purple-800',
          selectedBgColor: 'bg-purple-50/[1]',
          selectedBorderColor: 'border-purple-400',
          tslInfo: 'TSL Merged Momentum & Oscillator → Logic Block Required',
        };
      case BlockType.VOLATILITY:
        return {
          title: 'Volatility',
          icon: <BarChart3 className="w-4 h-4" />,
          bgColor: 'bg-orange-100/[1]',
          borderColor: 'border-orange-300',
          textColor: 'text-orange-800',
          selectedBgColor: 'bg-orange-50/[1]',
          selectedBorderColor: 'border-orange-400',
          tslInfo: 'TSL Volatility Indicator → Logic Block Required',
        };
      case BlockType.VOLUME:
        return {
          title: 'Volume',
          icon: <BarChart2 className="w-4 h-4" />,
          bgColor: 'bg-emerald-100/[1]',
          borderColor: 'border-emerald-300',
          textColor: 'text-emerald-800',
          selectedBgColor: 'bg-emerald-50/[1]',
          selectedBorderColor: 'border-emerald-400',
          tslInfo: 'TSL Volume Indicator → Logic Block Required',
        };
      case BlockType.TREND:
        return {
          title: 'Trend',
          icon: <TrendingUp className="w-4 h-4" />,
          bgColor: 'bg-teal-100/[1]',
          borderColor: 'border-teal-300',
          textColor: 'text-teal-800',
          selectedBgColor: 'bg-teal-50/[1]',
          selectedBorderColor: 'border-teal-400',
          tslInfo: 'TSL Trend Indicator → Logic Block Required',
        };
      case BlockType.CUSTOM:
        return {
          title: 'Custom Indicator',
          icon: <Code className="w-4 h-4" />,
          bgColor: 'bg-violet-100/[1]',
          borderColor: 'border-violet-300',
          textColor: 'text-violet-800',
          selectedBgColor: 'bg-violet-50/[1]',
          selectedBorderColor: 'border-violet-400',
          tslInfo: 'TSL Custom Indicator → Logic Block Required',
        };
      case BlockType.ENTRY:
        return {
          title: 'Entry Signal',
          icon: <ArrowDownToLine className="w-4 h-4" />,
          bgColor: 'bg-green-100/[1]',
          borderColor: 'border-green-300',
          textColor: 'text-green-800',
          selectedBgColor: 'bg-green-50/[1]',
          selectedBorderColor: 'border-green-400',
          tslInfo: 'TSL Terminal Block (No Outputs)',
        };
      case BlockType.EXIT:
        return {
          title: 'Exit Signal',
          icon: <ArrowUpFromLine className="w-4 h-4" />,
          bgColor: 'bg-orange-100/[1]',
          borderColor: 'border-orange-300',
          textColor: 'text-orange-800',
          selectedBgColor: 'bg-orange-50/[1]',
          selectedBorderColor: 'border-orange-400',
          tslInfo: 'TSL Terminal Block (No Outputs)',
        };
      case BlockType.STOP_LOSS:
        return {
          title: 'Stop Loss',
          icon: <Ban className="w-4 h-4" />,
          bgColor: 'bg-red-100/[1]',
          borderColor: 'border-red-300',
          textColor: 'text-red-800',
          selectedBgColor: 'bg-red-50/[1]',
          selectedBorderColor: 'border-red-400',
          tslInfo: 'TSL Terminal Block (No Outputs)',
        };
      case BlockType.TAKE_PROFIT:
        return {
          title: 'Take Profit',
          icon: <Target className="w-4 h-4" />,
          bgColor: 'bg-blue-100/[1]',
          borderColor: 'border-blue-300',
          textColor: 'text-blue-800',
          selectedBgColor: 'bg-blue-50/[1]',
          selectedBorderColor: 'border-blue-400',
          tslInfo: 'TSL Terminal Block (No Outputs)',
        };
      case BlockType.TIME_FILTER:
        return {
          title: 'Time Filter',
          icon: <Clock className="w-4 h-4" />,
          bgColor: 'bg-yellow-100/[1]',
          borderColor: 'border-yellow-300',
          textColor: 'text-yellow-800',
          selectedBgColor: 'bg-yellow-50/[1]',
          selectedBorderColor: 'border-yellow-400',
          tslInfo: 'TSL Market Filter → Logic Block Required',
        };
      case BlockType.LOGIC_GATE:
        return {
          title: 'Logic Gate',
          icon: <Workflow className="w-4 h-4" />,
          bgColor: 'bg-indigo-100/[1]',
          borderColor: 'border-indigo-300',
          textColor: 'text-indigo-800',
          selectedBgColor: 'bg-indigo-50/[1]',
          selectedBorderColor: 'border-indigo-400',
          tslInfo: 'TSL Logic Block (Can Chain)',
        };
      case BlockType.EXPRESSION:
        return {
          title: 'Expression',
          icon: <Calculator className="w-4 h-4" />,
          bgColor: 'bg-teal-100/[1]',
          borderColor: 'border-teal-300',
          textColor: 'text-teal-800',
          selectedBgColor: 'bg-teal-50/[1]',
          selectedBorderColor: 'border-teal-400',
          tslInfo: 'TSL Logic Block (Can Chain)',
        };
      // TSL Pattern Recognition blocks (3 separate types)
      case BlockType.CHART_PATTERNS:
        return {
          title: 'Chart Patterns',
          icon: <BarChart className="w-4 h-4" />,
          bgColor: 'bg-rose-100/[1]',
          borderColor: 'border-rose-300',
          textColor: 'text-rose-800',
          selectedBgColor: 'bg-rose-50/[1]',
          selectedBorderColor: 'border-rose-400',
          tslInfo: 'TSL Chart Pattern Recognition → Logic Block Required',
        };
      case BlockType.CANDLESTICK_PATTERNS:
        return {
          title: 'Candlestick Patterns',
          icon: <TrendingDown className="w-4 h-4" />,
          bgColor: 'bg-pink-100/[1]',
          borderColor: 'border-pink-300',
          textColor: 'text-pink-800',
          selectedBgColor: 'bg-pink-50/[1]',
          selectedBorderColor: 'border-pink-400',
          tslInfo: 'TSL Candlestick Pattern Recognition → Logic Block Required',
        };
      // M-A — duplicate-case removed. `case BlockType.CUSTOM` already
      // returned the "Custom Indicator" styling earlier in this switch
      // (line ~198); this second occurrence ("Custom Block") was dead code.
      // Same `BlockType.CUSTOM` enum splits two domain concepts — slated
      // for proper enum split in a future refactor.
      default:
        return {
          title: 'Unknown Block',
          icon: null,
          bgColor: 'bg-gray-100/[1]',
          borderColor: 'border-gray-300',
          textColor: 'text-gray-800',
          selectedBgColor: 'bg-gray-50/[1]',
          selectedBorderColor: 'border-gray-400',
          tslInfo: 'Unknown TSL Block Type',
        };
    }
  };

  const getConfigurationSummary = (block: Block): string => {
    switch (block.type) {
      case BlockType.STRATEGY_CONFIG:
        // S4e (2026-06-02): currency suffix removed from summary (dead
        // display-only field; engine doesn't price by currency). See
        // docs/BLOCK_SETTINGS_AUDIT.md S4e section.
        return `Capital: ${block.data.initial_capital}, Size: ${block.data.default_qty_value} ${block.data.default_qty_type}`;

      case BlockType.PRICE_DATA: {
        // S4f (2026-06-02): `symbol` read removed from the summary. The
        // field has no engine effect (backtest uses run-time selected
        // symbol), so surfacing it in the block summary was misleading.
        // See docs/BLOCK_SETTINGS_AUDIT.md S4f section.
        const timeframe = block.data.timeframe || 'H1';
        const source = block.data.source || 'close';
        const shift = block.data.shift;

        // S4a (2026-06-02): the `lookback` summary line was removed — the
        // field itself has no engine effect (the backtest engine always
        // materializes the full candle series), so surfacing it in the
        // block summary was misleading. See docs/BLOCK_SETTINGS_AUDIT.md.
        let priceDataSummary = `${timeframe}, Source: ${source}`;
        if (shift && shift !== 0) {
          priceDataSummary += `, Shift: ${shift > 0 ? '+' : ''}${shift}`;
        }
        return priceDataSummary;
      }

      case BlockType.MOVING_AVERAGE:
        return `Type: ${block.data.type || block.data.indicator}, Period: ${
          block.data.period
        }, Source: ${block.data.source}`;

      case BlockType.MOMENTUM_OSCILLATOR: {
        const indicator = block.data.indicator || block.data.type;
        let momentumSummary = `Type: ${indicator}`;

        switch (indicator) {
          case 'macd':
            momentumSummary += `, Fast: ${block.data.fastPeriod || 12}, Slow: ${
              block.data.slowPeriod || 26
            }, Signal: ${block.data.signalPeriod || 9}`;
            break;
          case 'stoch':
            momentumSummary += `, K: ${block.data.period || 14}, D: ${
              block.data.dPeriod || 3
            }`;
            if (block.data.kSmooth && block.data.kSmooth > 1) {
              momentumSummary += `, Smooth: ${block.data.kSmooth}`;
            }
            break;
          case 'ultimateOsc':
            momentumSummary += `, Periods: ${block.data.period1 || 7}/${
              block.data.period2 || 14
            }/${block.data.period3 || 28}`;
            break;
          case 'rsi':
          case 'cci':
          case 'mfi':
            momentumSummary += `, Period: ${block.data.period || 14}`;
            if (block.data.overbought || block.data.oversold) {
              momentumSummary += `, Levels: ${block.data.oversold || 30}/${
                block.data.overbought || 70
              }`;
            }
            break;
          default:
            momentumSummary += `, Period: ${block.data.period || 14}`;
            if (block.data.overbought || block.data.oversold) {
              momentumSummary += `, OB: ${block.data.overbought}, OS: ${block.data.oversold}`;
            }
        }
        return momentumSummary;
      }

      case BlockType.VOLATILITY: {
        const volIndicator = block.data.indicator || block.data.type;
        let volatilitySummary = `Type: ${volIndicator}, Period: ${block.data.period}`;
        if (block.data.multiplier) {
          volatilitySummary += `, Multiplier: ${block.data.multiplier}`;
        }
        if (block.data.stdDev) {
          volatilitySummary += `, StdDev: ${block.data.stdDev}`;
        }
        return volatilitySummary;
      }

      case BlockType.VOLUME: {
        const volumeIndicator = block.data.indicator || block.data.type;
        let volumeSummary = `Type: ${volumeIndicator}`;
        if (block.data.period) {
          volumeSummary += `, Period: ${block.data.period}`;
        }
        if (block.data.session && volumeIndicator === 'vwap') {
          volumeSummary += `, Reset: ${block.data.session}`;
        }
        return volumeSummary;
      }

      case BlockType.TREND: {
        const trendIndicator = block.data.indicator || block.data.type;
        let trendSummary = `Type: ${trendIndicator}, Period: ${block.data.period}`;
        if (block.data.threshold) {
          trendSummary += `, Threshold: ${block.data.threshold}`;
        }
        return trendSummary;
      }

      case BlockType.CUSTOM:
        // M-A — merged with a later duplicate that surfaced blockType +
        // outputTypes. Keeping the richer version since it shows the user
        // more about what the custom block does.
        // S4d (2026-06-02): the `outputTypes` segment removed from the
        // summary — field is gone from BlockData.
        return `Custom: ${block.data.name || 'Unnamed'}${
          block.data.blockType ? ` (${block.data.blockType})` : ''
        }${
          Object.keys(block.data.parameters || {}).length > 0
            ? `, ${Object.keys(block.data.parameters || {}).length} params`
            : ''
        }`;

      case BlockType.ENTRY:
        return `Type: ${block.data.entryType}, Direction: ${block.data.direction}, Logic: ${block.data.logic}`;

      case BlockType.EXIT:
        return `Type: ${block.data.exitType}, Logic: ${block.data.logic}${
          block.data.exitMode === ExitType.PARTIAL
            ? `, Partial: ${block.data.partialQuantity}%`
            : ''
        }`;

      case BlockType.STOP_LOSS:
        return `Type: ${block.data.stopType}, Value: ${block.data.stopValue}`;

      case BlockType.TAKE_PROFIT:
        return `Type: ${block.data.tpType}, Value: ${block.data.tpValue}`;

      case BlockType.TIME_FILTER:
        return `Type: ${block.data.filterType}`;

      case BlockType.LOGIC_GATE:
        return `Type: ${block.data.type}${
          block.data.invert ? ', Inverted' : ''
        }`;

      case BlockType.EXPRESSION:
        return `${
          block.data.leftSource === 'static' ? block.data.leftValue : 'Input'
        } ${block.data.operator} ${
          block.data.rightSource === 'static' ? block.data.rightValue : 'Input'
        }`;

      // TSL Pattern Recognition summaries
      case BlockType.CHART_PATTERNS: {
        const chartPatternCount = block.data.chartPatterns?.length || 0;
        return `Chart patterns: ${chartPatternCount} selected, Sensitivity: ${
          block.data.sensitivity || 'medium'
        }`;
      }

      case BlockType.CANDLESTICK_PATTERNS: {
        const candlePatternCount = block.data.candlestickPatterns?.length || 0;
        return `Candlestick patterns: ${candlePatternCount} selected, Sensitivity: ${
          block.data.sensitivity || 'medium'
        }`;
      }

      // M-A — second `case BlockType.CUSTOM` removed; richer fields merged
      // into the earlier case above so behaviour now matches what was
      // intended at the file's last edit.

      default:
        return '';
    }
  };

  const properties = getBlockProperties();
  const hasErrors = validationErrors.length > 0;
  const hasWarnings = validationWarnings.length > 0;

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        width: `${blockWidth}px`,
        left: `${block.position.x}px`,
        top: `${block.position.y}px`,
      }
    : {
        width: `${blockWidth}px`,
        left: `${block.position.x}px`,
        top: `${block.position.y}px`,
      };

  return (
    <Tooltip.Provider>
      <Tooltip.Root
        open={isSelected ? false : tooltipOpen}
        onOpenChange={setTooltipOpen}
      >
        <Tooltip.Trigger asChild>
          <div
            ref={setNodeRef}
            style={style}
            className={`
              absolute ${isDimmed ? 'opacity-40 grayscale' : 'opacity-100'} transition-all duration-200 overflow-hidden
              ${isSelected ? properties.selectedBgColor : properties.bgColor}
              ${
                isSelected
                  ? properties.selectedBorderColor
                  : properties.borderColor
              }
              border rounded-md shadow-md hover:shadow-lg
              ${
                isLocked
                  ? 'cursor-not-allowed opacity-75'
                  : readonly
                  ? 'cursor-default'
                  : 'cursor-grab active:cursor-grabbing'
              }
              ${isSelected ? 'ring-2 ring-blue-400 ring-opacity-50' : ''}
              ${hasErrors ? 'ring-2 ring-red-400 ring-opacity-50' : ''}
              ${
                hasWarnings && !hasErrors
                  ? 'ring-2 ring-yellow-400 ring-opacity-50'
                  : ''
              }
            `}
            onClick={onSelect}
            data-block-type={block.type}
            data-block-id={block.id}
          >
            {/* TSL: Input Connection Port (only if allowed) */}
            {!readonly && canHaveInputConnections && (
              <div
                className="absolute -left-2 top-6 w-4 h-4 bg-blue-500 rounded-full border-2 border-white shadow-md cursor-pointer hover:bg-blue-600 transition-colors z-10"
                data-drop-target={connectingPortType === 'output'}
                onMouseDown={handleInputPortMouseDown}
                onMouseUp={handleInputPortMouseUp}
                title="TSL Input Port"
              />
            )}

            {/* Header */}
            <div
              ref={headerRef}
              className={`flex items-center justify-between p-2 border-b ${properties.borderColor} min-h-[44px]`}
              {...dragHandleProps}
            >
              <div className="flex items-center space-x-2 flex-1 min-w-0">
                <span className={`${properties.textColor} flex-shrink-0`}>
                  {properties.icon}
                </span>
                <div className="flex flex-col flex-1 min-w-0" ref={contentRef}>
                  <h4
                    className={`text-sm font-medium ${properties.textColor} truncate`}
                    title={block.data.name || properties.title}
                  >
                    {block.data.name || properties.title}
                  </h4>
                  <span className="text-xs text-gray-500 truncate">
                    {properties.title}
                  </span>
                </div>
              </div>

              {/* Action Buttons Container */}
              {!readonly && (
                <div className="flex items-center space-x-1 flex-shrink-0 ml-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onToggleLock) {
                        setIsLocked(!isLocked);
                        onToggleLock(block.id);
                      }
                    }}
                    className={`p-1 rounded hover:bg-white/50 ${properties.textColor} transition-colors`}
                    title={isLocked ? 'Unlock TSL Block' : 'Lock TSL Block'}
                  >
                    {isLocked ? (
                      <Lock className="w-3.5 h-3.5" />
                    ) : (
                      <Unlock className="w-3.5 h-3.5" />
                    )}
                  </button>

                  {onDuplicate && block.type !== BlockType.STRATEGY_CONFIG && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate(block);
                      }}
                      className={`p-1 rounded hover:bg-white/50 ${properties.textColor} transition-colors`}
                      title="Duplicate TSL Block"
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                  )}

                  {/* S91 — Disable/Enable toggle. Non-destructive ablation:
                      excludes the block from the backtest + conversion +
                      validation without deleting it. Hidden for STRATEGY_CONFIG
                      (required). */}
                  {onToggleDisable && block.type !== BlockType.STRATEGY_CONFIG && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleDisable(block.id);
                      }}
                      className={`p-1 rounded hover:bg-white/50 ${properties.textColor} transition-colors`}
                      title={
                        isDisabled
                          ? 'Enable TSL Block (include it in the backtest again)'
                          : 'Disable TSL Block (exclude from backtest — non-destructive, toggle back anytime)'
                      }
                    >
                      {isDisabled ? (
                        <PowerOff className="w-3.5 h-3.5" />
                      ) : (
                        <Power className="w-3.5 h-3.5" />
                      )}
                    </button>
                  )}

                  {onDelete && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(block.id);
                      }}
                      className={`p-1 rounded hover:bg-white/50 ${properties.textColor} transition-colors`}
                      title="Delete TSL Block"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-2">
              <div className="flex items-center space-x-1 mb-1">
                <Settings className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-600 truncate">
                  {getConfigurationSummary(block)}
                </span>
              </div>

              <p
                className={`text-xs ${properties.textColor} opacity-80 break-words line-clamp-2`}
              >
                {block.data.description ||
                  `Configure your ${properties.title.toLowerCase()}`}
              </p>

              {/* TSL-specific info */}
              {properties.tslInfo && (
                <div className="mt-2 p-1 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                  {properties.tslInfo}
                </div>
              )}

              {/* TSL Validation Indicators */}
              {(hasErrors || hasWarnings) && (
                <div className="mt-2 flex items-center space-x-1">
                  {hasErrors && (
                    <div className="flex items-center text-red-600">
                      <span className="text-xs">
                        ⚠ {validationErrors.length} TSL error
                        {validationErrors.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                  {hasWarnings && (
                    <div className="flex items-center text-yellow-600">
                      <span className="text-xs">
                        ⚠ {validationWarnings.length} TSL warning
                        {validationWarnings.length > 1 ? 's' : ''}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* TSL: Output Connection Port (only if allowed) */}
            {!readonly && canHaveOutputConnections && (
              <div
                className="absolute -right-2 top-6 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-md cursor-pointer hover:bg-green-600 transition-colors z-10"
                data-drop-target={connectingPortType === 'input'}
                onMouseDown={handleOutputPortMouseDown}
                onMouseUp={handleOutputPortMouseUp}
                title="TSL Output Port"
              />
            )}

            {/* TSL Terminal Block Indicator */}
            {isTerminalBlock(block.type) && (
              <div className="absolute right-1 top-1 w-3 h-3 bg-red-500 rounded-full border border-white text-white text-xs flex items-center justify-center">
                <span className="text-[8px] font-bold">T</span>
              </div>
            )}

            {/* TSL Strategy Config No-Connection Indicator */}
            {block.type === BlockType.STRATEGY_CONFIG && (
              <div className="absolute -left-1 -top-1 w-3 h-3 bg-cyan-500 rounded-full border border-white text-white text-xs flex items-center justify-center">
                <span className="text-[8px] font-bold">S</span>
              </div>
            )}
          </div>
        </Tooltip.Trigger>
        <Tooltip.Portal>
          <Tooltip.Content
            className="max-w-xs bg-gray-900 text-white text-sm px-3 py-2 rounded shadow-lg z-50"
            sideOffset={5}
            collisionPadding={8}
          >
            <div className="space-y-1">
              <div className="font-medium">
                {block.data.name || properties.title}
              </div>
              <div className="text-gray-300">
                {getConfigurationSummary(block)}
              </div>
              {block.data.description && (
                <div className="text-gray-400">{block.data.description}</div>
              )}
              {properties.tslInfo && (
                <div className="text-blue-300 border-t border-gray-700 pt-1">
                  TSL: {properties.tslInfo}
                </div>
              )}
              {hasErrors && (
                <div className="text-red-400">
                  {validationErrors.length} TSL validation error
                  {validationErrors.length > 1 ? 's' : ''}
                </div>
              )}
              {hasWarnings && (
                <div className="text-yellow-400">
                  {validationWarnings.length} TSL warning
                  {validationWarnings.length > 1 ? 's' : ''}
                </div>
              )}
              {block.type === BlockType.STRATEGY_CONFIG && (
                <div className="text-cyan-300 border-t border-gray-700 pt-1">
                  This block configures the entire strategy and cannot be
                  connected to other blocks
                </div>
              )}
              {isTerminalBlock(block.type) && (
                <div className="text-green-300 border-t border-gray-700 pt-1">
                  Terminal block: receives signals but produces no outputs
                </div>
              )}
            </div>
            <Tooltip.Arrow className="fill-gray-900" />
          </Tooltip.Content>
        </Tooltip.Portal>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
};

export default StrategyBlock;
